import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { blogArticles, type BlogArticle } from '@/mocks/blog-articles';

function ArticleNavbar() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-background-50/98 backdrop-blur-md border-b border-secondary-200/60' : 'bg-background-50/90 backdrop-blur-md border-b border-secondary-100/40'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="text-foreground-950 font-bold text-xl tracking-tight whitespace-nowrap hover:text-primary-600 transition-colors duration-200">
            itaobuy
          </Link>
          <div className="flex items-center gap-5">
            <Link to="/" className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap">
              Home
            </Link>
            <Link to="/blog" className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap">
              Shopping Guide
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-5 py-2 rounded-[10px] bg-foreground-950 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              buy it now
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}

function ArticleFooter() {
  return (
    <footer className="bg-foreground-950 pt-16 pb-8">
      <div className="w-full px-6 md:px-10">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
          <h3 className="text-2xl font-bold text-white">itaobuy</h3>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-white/60 text-sm hover:text-white transition-colors duration-200 whitespace-nowrap">
              Back to Home
            </Link>
            <Link to="/blog" className="text-white/60 text-sm hover:text-white transition-colors duration-200 whitespace-nowrap">
              Shopping Guide
            </Link>
          </div>
        </div>
        <div className="border-t border-white/10 pt-6">
          <p className="text-white/40 text-xs text-center">
            © 2026 itaobuy.boats. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<BlogArticle | null>(null);
  const [notFound, setNotFound] = useState(false);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://itaobuy.boats';
  const logoUrl = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg';

  // Scroll to top on mount and when slug changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    const found = blogArticles.find((a) => a.slug === slug);
    if (found) {
      setArticle(found);
      setNotFound(false);
    } else {
      setNotFound(true);
    }
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors duration-200">
            ← Back to Home
          </Link>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* Schema.org Article */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.metaDescription,
            "author": {
              "@type": "Person",
              "name": article.author,
              "jobTitle": article.authorRole
            },
            "datePublished": "2026-06-27",
            "dateModified": "2026-06-27",
            "image": article.imageUrl,
            "publisher": {
              "@type": "Organization",
              "name": "Itaobuy Spreadsheet",
              "logo": {
                "@type": "ImageObject",
                "url": logoUrl
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `${siteUrl}/article/${article.slug}`
            }
          })
        }}
      />
      <ArticleNavbar />
      <main>
        {/* Article Hero */}
        <section className="relative pt-24 pb-16 md:pt-32 md:pb-20 bg-background-50 overflow-hidden">
          <div className="absolute top-20 right-[10%] w-[400px] h-[400px] rounded-full bg-accent-50/50 blur-[100px] pointer-events-none"></div>

          <div className="w-full px-6 md:px-10 flex flex-col lg:flex-row items-center gap-10 lg:gap-20 relative z-10">
            <div className="w-full lg:w-[40%]">
              <Link
                to="/blog"
                className="inline-flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200 mb-6"
              >
                <i className="ri-arrow-left-line"></i>
                ← Back to Shopping Guide
              </Link>
              <div className="flex items-center gap-2 mb-3">
                <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-500">{article.date}</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 leading-[1.15] tracking-tight">
                {article.title}
              </h1>
              <p className="mt-4 text-sm text-foreground-500">
                By {article.author} · {article.authorRole}
              </p>
              <p className="mt-4 text-base text-foreground-600 leading-relaxed max-w-lg">
                {article.metaDescription}
              </p>
            </div>

            <div className="w-full lg:w-[60%]">
              <div className="rounded-3xl overflow-hidden bg-white">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Introduction + Quote */}
        <section className="py-16 md:py-20 bg-white">
          <div className="w-full px-6 md:px-10 flex flex-col lg:flex-row gap-12 lg:gap-16">
            <div className="w-full lg:w-[45%]">
              <span className="inline-block px-3 py-1 rounded-md bg-secondary-200 text-xs font-medium text-foreground-600 mb-6">
                Introduction
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 leading-tight mb-6">
                Understanding {article.category === 'Guide' ? 'ItaobuySpreadsheet' : article.category}
              </h2>
              <p className="text-base text-foreground-600 leading-relaxed font-light">
                {article.introText}
              </p>
            </div>

            <div className="w-full lg:w-[50%]">
              <div className="rounded-[32px] bg-primary-500 p-10 md:p-12">
                <blockquote className="text-background-50 text-lg leading-relaxed font-light">
                  "{article.quote}"
                </blockquote>
                <div className="mt-6 flex items-center gap-3">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20east%20asian%20male%20entrepreneur%20with%20glasses%20on%20clean%20white%20studio%20background%20friendly%20confident%20smile%20modern%20business%20casual%20blazer%20sharp%20professional%20look&width=96&height=96&seq=article-author-avatar&orientation=squarish"
                    alt={article.author}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-background-50 font-medium text-sm">{article.author}</p>
                    <p className="text-background-50/50 text-xs">{article.authorRole}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Article Sections */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-6 md:px-10 max-w-3xl mx-auto">
            <article>
              {article.sections.map((section, idx) => (
                <div key={idx} className="mb-10">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                    {section.heading}
                  </h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    {section.content}
                  </p>
                </div>
              ))}
            </article>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative h-[450px] md:h-[500px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20creative%20workspace%20with%20laptop%20and%20product%20samples%20on%20light%20wood%20desk%20soft%20natural%20afternoon%20sunlight%20from%20window%20clean%20modern%20aesthetic%20cozy%20atmosphere%20with%20subtle%20green%20plants&width=1600&height=900&seq=article-cta-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
          <div className="relative z-10 text-center px-6 w-full max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-wide">
              START SOURCING SMARTER
            </h2>
            <p className="mt-4 text-white/80 text-base leading-relaxed">
              Join thousands of sellers using Itaobuy Sheet
              <br />
              to scale their business profitably.
            </p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center mt-8 px-8 py-4 rounded-full bg-foreground-950 text-background-50 font-semibold text-sm tracking-wide hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer relative"
            >
              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-accent-500 flex items-center justify-center -ml-1">
                <i className="ri-shopping-bag-line text-white text-lg"></i>
              </span>
              <span className="pl-10 pr-2">GET ITAOBUY SHEET NOW</span>
              <i className="ri-arrow-right-up-line text-lg"></i>
            </a>
          </div>
        </section>
      </main>
      <ArticleFooter />
    </div>
  );
}